import React from 'react';
import { ArrowLeft } from 'lucide-react';

const Section = ({ number, title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-xl font-semibold text-primary mb-4">
      {number}. {title}
    </h2>
    {children}
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="font-heading font-semibold text-text-main mb-2 mt-5">{children}</h3>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-text-main text-sm leading-relaxed">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Para = ({ children }) => (
  <p className="text-text-main text-sm leading-relaxed mb-3">{children}</p>
);

const Example = ({ good, children }) => (
  <div
    className="flex items-start gap-2.5 text-sm leading-relaxed rounded-arch px-4 py-3 mb-2"
    style={good
      ? { background: 'rgba(74,173,168,0.08)', border: '1px solid rgba(74,173,168,0.2)' }
      : { background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.18)' }}
  >
    <span
      className="font-heading font-semibold shrink-0"
      style={{ color: good ? '#2a7b77' : '#b91c1c' }}
    >
      {good ? 'OK' : 'Not OK'}
    </span>
    <span className="text-text-main">{children}</span>
  </div>
);

const MentorTermsPage = ({ onNavigate }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Subtle teal-to-lavender gradient strip at top */}
      <div className="h-1 w-full" style={{ background: 'linear-gradient(to right, #4AADA8, #A99CC8)' }} aria-hidden="true" />

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Back button */}
        {onNavigate && (
          <button
            onClick={() => onNavigate('feed')}
            aria-label="Back to feed"
            className="flex items-center gap-2 text-sm text-text-muted hover:text-primary transition-colors mb-10 min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to Sirati
          </button>
        )}

        {/* Header */}
        <header className="text-center mb-12">
          <p className="font-heading text-3xl font-bold tracking-wide text-primary mb-1">SIRATI</p>
          <h1 className="font-heading text-2xl font-semibold mb-2" style={{ color: '#A99CC8' }}>Mentor Agreement</h1>
          <p className="text-xs text-text-muted">Effective Date: August 2026 &nbsp;|&nbsp; Last Updated: August 2026</p>
        </header>

        {/* Divider */}
        <div className="h-px mb-10" style={{ background: 'linear-gradient(to right, transparent, #4AADA820, #A99CC830, #4AADA820, transparent)' }} aria-hidden="true" />

        {/* Agreement body */}
        <div>

          <Section number="1" title="What This Is">
            <Para>
              This page describes what Sirati expects from mentors, and what mentors can expect from us. By accepting a mentor role on Sirati, you agree to act in line with what's described here.
            </Para>
            <Para>
              This is not a formal legal contract, and it doesn't create an employment relationship. You can step down as a mentor at any time, for any reason, no questions asked—just let us know. Similarly, Sirati may end a mentor relationship at any time, particularly if these guidelines aren't being followed.
            </Para>
          </Section>

          <Section number="2" title="Your Role">
            <Para>
              As a Sirati Mentor, members may reach out to you with real, personal questions about marriage readiness, relationships, and their growth journey. We ask that you:
            </Para>
            <BulletList items={[
              'Answer thoughtfully and respectfully, in line with Sirati’s values as a faith-centered community.',
              'Keep communication with members professional at all times.',
              'Represent Sirati and yourself well—mentors are visible, trusted figures in the community.',
              'Work with the Sirati team when questions/concerns come up, rather than making unilateral calls on gray areas.',
            ]} />
          </Section>

          <Section number="3" title="No Poaching or Off-Platform Solicitation">
            <Para>
              It's natural that members you help may be curious about your background, or that you offer coaching or services elsewhere. That's fine, but the Sirati community and its Q&amp;A features can't be used as a funnel to move members off the platform and into a mentor's paid (or un-paid) offers without the green light from our team.
            </Para>

            <SubHeading>For example:</SubHeading>
            <Example good>
              A member asks for advice in a community post. You reply publicly with genuinely helpful guidance. That's exactly what mentors are here for. If a community member sees your advice and reaches out to you to inquire about your services, that's great!
            </Example>
            <Example>
              A member asks for advice, and your reply steers them toward your own business: "DM me," "follow me on Instagram," "book a call at [your website]." This isn't allowed, whether it happens in a public post, a mentor Q&amp;A answer, or a direct message.
            </Example>

            <Para>
              If you're interested in a more formal partnership or promotional arrangement with Sirati, for example, offering your coaching services to our members through an approved channel—we're open to that conversation. Just reach out to us first rather than arranging it independently within the community.
            </Para>
          </Section>

          <Section number="4" title="Professional Conduct &amp; Personal Relationships">
            <Para>
              Mentors hold a position of trust. Communication with members through your mentor role must stay professional—personal or romantic advances toward a community member you've connected with (or haven't connected with) as a mentor are not permitted.
            </Para>
            <Para>
              If a genuine personal connection develops and you'd like to pursue it, you must step down from the mentor role first, before pursuing anything personal. Using your position as a mentor to pursue a personal or romantic connection with a community member—rather than stepping down first—is a violation of our community guidelines and mentor agreement. Such actions will result in immediate removal from the mentor role and from the Sirati community entirely.
            </Para>
          </Section>

          <Section number="5" title="Compensation">
            <Para>
              Mentor compensation details (rate and how payouts work) are provided to you directly by Sirati and may be updated from time to time. Since this isn't a contract, compensation terms can change with notice, and either side can end the arrangement at any time as described above.
            </Para>
          </Section>

          <Section number="6" title="Questions">
            <Para>
              If anything here is unclear, or a situation comes up that isn't covered, reach out to us directly rather than guessing—we're happy to provide any clarity you need.
            </Para>
            <div className="mt-3 p-4 rounded-arch-lg border border-primary/20 bg-primary/5 text-sm text-text-main space-y-1">
              <p><span className="font-medium text-primary">Sirati</span></p>
              <p>Email: <a href="mailto:salam@sirati.co" className="text-primary hover:underline">salam@sirati.co</a></p>
              <p>Website: <a href="https://www.sirati.co" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.sirati.co</a></p>
            </div>
          </Section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-xs text-text-muted">
            &copy; {new Date().getFullYear()} Sirati. 🌙 All Rights Reserved.
          </p>
          {onNavigate && (
            <button
              onClick={() => onNavigate('feed')}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-opacity hover:opacity-90 min-h-[44px]"
              style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }}
            >
              Return to Sirati
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentorTermsPage;
